'use client'

import { Badge } from '@brew/ui/badge'
import { cn } from '@brew/ui/cn'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { capitalize } from '@/lib/shared/text/capitalize'
import { appToast } from '@/lib/shared/toast'

type Visibility = 'public' | 'private'

interface VisibilityToggleBadgeProps {
  emailId: string
  currentVisibility: string
}

const VISIBILITY_OPTIONS: Array<{ value: Visibility; label: string }> = [
  { value: 'public', label: 'Public' },
  { value: 'private', label: 'Private' },
]

function normalizeVisibility(value: string): Visibility {
  return value === 'public' ? 'public' : 'private'
}

export function VisibilityToggleBadge({
  emailId,
  currentVisibility,
}: VisibilityToggleBadgeProps) {
  const router = useRouter()
  const [visibility, setVisibility] = useState<Visibility>(
    normalizeVisibility(currentVisibility)
  )
  const [isOpen, setIsOpen] = useState(false)
  const [isPending, setIsPending] = useState(false)

  const handleSelect = async (newVisibility: Visibility) => {
    if (isPending || newVisibility === visibility) {
      setIsOpen(false)
      return
    }
    const previous = visibility
    setVisibility(newVisibility)
    setIsOpen(false)
    setIsPending(true)
    try {
      const response = await fetch(`/api/templates/${emailId}/visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility: newVisibility }),
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as {
          error?: string
        }
        setVisibility(previous)
        appToast.error(payload.error ?? 'Failed to update visibility')
        return
      }
      // Re-stream the RSC tree so the page's `currentVisibility` prop and
      // any downstream caches reflect the new server state seamlessly.
      router.refresh()
    } catch {
      setVisibility(previous)
      appToast.error('Failed to update visibility')
    } finally {
      setIsPending(false)
    }
  }

  const isPublic = visibility === 'public'

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="cursor-pointer"
        disabled={isPending}
      >
        <Badge
          variant="outline"
          className={cn({
            'border-emerald-500/40 bg-emerald-500/10 text-emerald-600':
              isPublic,
            'border-border text-muted-foreground': !isPublic,
          })}
        >
          {capitalize(visibility)}
        </Badge>
      </button>

      {isOpen ? (
        <div className="absolute top-full right-0 z-50 mt-1 min-w-[120px] rounded-lg border border-border bg-popover p-1">
          {VISIBILITY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'w-full rounded-sm px-3 py-1.5 text-left text-xs transition-colors',
                {
                  'bg-muted font-medium': visibility === option.value,
                  'hover:bg-muted/50': visibility !== option.value,
                }
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
